import { useCallback, useState } from "react"
import CloudDownloadRoundedIcon from "@mui/icons-material/CloudDownloadRounded"
import { IconButton, Tooltip, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next"
import { CSVLink } from "react-csv"
import fp from "lodash/fp"
import { DateTime } from "luxon"
import type { Headers } from "react-csv/lib/core"

import type { RecordWTimestamp, RecordType } from "types/dashboard.types"
import type { EntityType } from "types/common.types"

import { formatXYPlotData } from "helpers/formatters/dataFormatters"
import { CSV_COLUMN_KEYS } from "helpers/utils/constants"
import { generateCSVFilename } from "helpers/utils/plots"

interface PlotCsvDownloadProps {
  plotData: RecordType[]
  csvData: {
    nameKey: string
    entity: EntityType
    entityIds: number[]
    fromDate: string
    toDate: string
  }
}

const PlotCsvDownload = ({ plotData, csvData }: PlotCsvDownloadProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { entity, entityIds, nameKey, fromDate, toDate } = csvData
  const [data, setData] = useState<RecordType[]>([])
  const [headers, setHeaders] = useState<Headers>()
  const [filename, setFilename] = useState<string>()

  const handleClick = useCallback(() => {
    const formattedPlotData = formatXYPlotData(plotData)

    const formattedData = fp.flow(
      fp.map((data: RecordWTimestamp) => fp.pick(CSV_COLUMN_KEYS, data)),
      fp.map((data) => ({
        ...data,
        _value: Number(data._value),
        _time: DateTime.fromJSDate(data._time as Date).toFormat("yyyy-MM-dd HH:mm"),
      })),
    )(formattedPlotData)

    const formattedHeaders = plotData.length
      ? fp.flow(
          fp.pick(CSV_COLUMN_KEYS),
          fp.keys,
          fp.map((key) => ({ label: key, key })),
        )(plotData[0])
      : []

    const formattedFromDate = DateTime.fromISO(fromDate).toFormat("yyyy-MM-dd HH:mm:ss")
    const formattedtoDate = DateTime.fromISO(toDate).toFormat("yyyy-MM-dd HH:mm:ss")

    const formattedFilename = generateCSVFilename(
      entity,
      entityIds,
      nameKey,
      formattedFromDate,
      formattedtoDate,
      t,
    )

    setData(formattedData)
    setHeaders(formattedHeaders)
    setFilename(formattedFilename)
  }, [plotData, fromDate, toDate, t, entity, entityIds, nameKey])

  return (
    <CSVLink
      onClick={handleClick}
      data={data}
      headers={headers}
      filename={filename}
      uFEFF
    >
      <Tooltip title={t("table.DOWNLOAD_CSV")} arrow>
        <IconButton
          disableRipple
          sx={{
            padding: 0,
            "&:hover": {
              color: theme.palette.primary.main,
            },
          }}
        >
          <CloudDownloadRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </CSVLink>
  )
}

export default PlotCsvDownload
